/**
 * ConstructionMethodPanel — native rewrite of the legacy ConstructionMethod
 * sub-tab (Shape / Roof / Floor / Basement / Window / ThermalBridges).
 *
 * ⚠ Insulation SWAP: the roof-insulation input drives `setSubsequentRoofInsulation`
 * and the basement-insulation input drives `setSubsequentBasementInsulation`; the
 * codec crosses them onto the wire (reproduced) and decodes them back
 * symmetrically (C4), so the user sees roof↔roof / basement↔basement.
 */

import React from 'react'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { Trans, useTranslation } from 'react-i18next'

import {
  BasementCeilingType,
  BasementType,
  ConstructionType,
  LightweightConstructionType,
  RoofConditionType,
  RoofPitchType,
  SolidConstructionType,
  ThermalBridgeType,
  WindowType
} from 'constants/purchaseFlow'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { Label } from 'components/common/Label'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUIInputFieldCounter } from 'components/common/MUIInputFieldCounter'
import { MUINumberField } from 'components/common/MUINumberField'
import { MUIRadio } from 'components/common/MUIRadio'
import { SectionedBorderBox } from 'components/common/SectionedBorderBox'
import { ToggleButton } from 'components/common/ToggleButton'

import type { UseEnergyCertificateForm } from '../useEnergyCertificateForm'
import { NumericFloatField } from 'components/common/MUIFloatNumberField'

const HEATED = new Set<BasementType>([BasementType.HEATED_WINDOW_SOLAR_GAIN, BasementType.HEATED_NO_WINDOW])

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="text-md" fontWeight={600} color={GRAY_900}>{children}</Typography>
)

export const ConstructionMethodPanel: React.FC<{ form: UseEnergyCertificateForm }> = ({ form }) => {
  const { t } = useTranslation(['purchase_flow'], { keyPrefix: 'energy_certificate_step.construction_method_page' })
  const {
    view,
    setConstructionType,
    setLightweightConstructionType,
    setSolidConstructionType,
    setRoofCondition,
    setRoofPitch,
    setSubsequentRoofInsulation,
    setSubsequentBasementInsulation,
    setBasementType,
    setBasementCeilingType,
    setSoilDepth,
    setHeatedBasementArea,
    setWindowType,
    setYearOfManufacture,
    setNumberOfFullFloors,
    setFloorHeight,
    setThermalBridge,
  } = form

  const isMassive = view.constructionType === ConstructionType.MASSIVE_CONSTRUCTION
  const selectedConstructionSubtype = isMassive ? view.solidConstructionType : view.lightweightConstructionType
  const isBasementHeated = view.basementType != null && HEATED.has(view.basementType)

  return (
    <Stack gap={1.6}>

      {/* SHAPE */}
      <SectionedBorderBox title={<SectionTitle>{t('shape_title')}</SectionTitle>}>
        <Stack alignItems="flex-start" gap={1.2}>

          <Label text={t('global_shape')} />

          <Stack gap={0.8} flexDirection="row" flexWrap="wrap">
            {Object.values(ConstructionType).map((type) => (
              <ToggleButton
                key={type}
                isSelected={view.constructionType === type}
                label={t(`global_shape_type.${type}`)}
                sx={{ width: '21.6rem', height: '4rem', justifyContent: 'center' }}
                onClick={() => setConstructionType(type)}
                typographyVariant="text-md"
              />
            ))}
          </Stack>

          <Stack gap="1.8rem" width={{ xs: '100%', lg: '55rem' }} position="relative">
            <MUIDropdown
              sx={{ width: '100%' }}
              button={(_isOpen, action) => (
                <MUIDropdownInput
                  readOnly
                  required
                  label={t(`global_shape_type.${view.constructionType}`)}
                  type={DropdownInputTypes.SELECTOR}
                  value={t(
                    selectedConstructionSubtype
                      ? `${isMassive ? 'solid' : 'lightweight'}_construction_type.${selectedConstructionSubtype}`
                      : isMassive
                        ? 'select_solid_construction_option'
                        : 'select_lightweight_construction_option'
                  )}
                  onClick={action}
                />
              )}
            >
              {Object.values(isMassive ? SolidConstructionType : LightweightConstructionType).map((type) => (
                <MUIDropdownItem
                  key={type}
                  onClick={() => (isMassive ? setSolidConstructionType(type as SolidConstructionType) : setLightweightConstructionType(type as LightweightConstructionType))}
                >
                  <Typography variant="text-md" color={GRAY_900} fontWeight="medium">{t(`${isMassive ? 'solid' : 'lightweight'}_construction_type.${type}`)}</Typography>
                </MUIDropdownItem>
              ))}
            </MUIDropdown>
          </Stack>

        </Stack>
      </SectionedBorderBox>

      {/* ROOF */}
      <SectionedBorderBox title={<SectionTitle>{t('roof_title')}</SectionTitle>}>
        <Stack alignItems="flex-start" gap={1.2}>

          <Stack gap="1.8rem" width={{ xs: '100%', lg: '32rem' }} position="relative">
            <MUIDropdown
              sx={{ width: '100%' }}
              button={(_isOpen, action) => (
                <MUIDropdownInput
                  readOnly
                  required
                  label={t('roof_condition')}
                  type={DropdownInputTypes.SELECTOR}
                  value={t(view.roofConditionType ? `roof_condition_type.${view.roofConditionType}` : 'select_roof_condition_option')}
                  onClick={action}
                />
              )}
            >
              {Object.values(RoofConditionType).map((type) => (
                <MUIDropdownItem key={type} onClick={() => setRoofCondition(type)}>
                  <Typography variant="text-md" color={GRAY_900} fontWeight="medium">{t(`roof_condition_type.${type}`)}</Typography>
                </MUIDropdownItem>
              ))}
            </MUIDropdown>
          </Stack>

          <Stack gap=".8rem">
            <Label color={GRAY_700} text={t('roof_pitch')} />
            {Object.values(RoofPitchType).map((type) => (
              <Stack key={type} gap=".8rem" flexDirection="row">
                <MUIRadio checked={view.roofPitchType === type} onChange={() => setRoofPitch(type)} />
                <Label color={GRAY_900} text={t(`roof_pitch_type.${type}`)} />
              </Stack>
            ))}
          </Stack>

          <Stack marginTop={0.8}>
            <MUINumberField
              min={0}
              suffix={t('cm')}
              inputBoxWidth="100%"
              label={t('insulation_roof')}
              value={view.subsequentRoofInsulation}
              onChange={setSubsequentRoofInsulation}
            />
          </Stack>

        </Stack>
      </SectionedBorderBox>

      {/* FLOOR */}
      <SectionedBorderBox title={<SectionTitle>{t('floors_title')}</SectionTitle>}>
        <Stack alignItems="flex-start" gap={1.2}>

          <MUIInputFieldCounter
            required
            counterValue={view.numberOfFullFloors ?? 1}
            counterOnChange={(value) => setNumberOfFullFloors(value ?? 1)}
            inputBoxWidth="14rem"
            label={t('number_of_floors')}
          />

          <Stack>
            <NumericFloatField
              required
              suffix="M"
              inputBoxWidth="100%"
              label={t('floor_height')}
              value={view.floorHeight}
              onCommit={setFloorHeight}
            />
            <Typography variant="text-xs" fontWeight="medium" color={GRAY_700}>{t('averaging_multiple_floors')}</Typography>
          </Stack>

        </Stack>
      </SectionedBorderBox>

      {/* BASEMENT */}
      <SectionedBorderBox title={<SectionTitle>{t('basement_title')}</SectionTitle>}>
        <Stack alignItems="flex-start" gap={1.2}>

          <Stack gap="1.8rem" width={{ xs: '100%', lg: '32rem' }} position="relative">
            <MUIDropdown
              sx={{ width: '100%' }}
              button={(_isOpen, action) => (
                <MUIDropdownInput
                  readOnly
                  required
                  label={t('basement_cellar')}
                  type={DropdownInputTypes.SELECTOR}
                  value={t(view.basementType ? `basement_type.${view.basementType}` : 'select_basement_cellar_option')}
                  onClick={action}
                />
              )}
            >
              {Object.values(BasementType).map((type) => (
                <MUIDropdownItem key={type} onClick={() => setBasementType(type)}>
                  <Typography variant="text-md" color={GRAY_900} fontWeight="medium">{t(`basement_type.${type}`)}</Typography>
                </MUIDropdownItem>
              ))}
            </MUIDropdown>
          </Stack>

          <Stack gap="1.8rem" width={{ xs: '100%', lg: '32rem' }} position="relative">
            <MUIDropdown
              sx={{ width: '100%' }}
              button={(_isOpen, action) => (
                <MUIDropdownInput
                  readOnly
                  required
                  label={t('basement_ceiling_floor')}
                  type={DropdownInputTypes.SELECTOR}
                  value={t(view.basementCeilingType ? `basement_ceiling_floor_type.${view.basementCeilingType}` : 'select_basement_ceiling_option')}
                  onClick={action}
                />
              )}
            >
              {Object.values(BasementCeilingType).map((type) => (
                <MUIDropdownItem key={type} onClick={() => setBasementCeilingType(type)}>
                  <Typography variant="text-md" color={GRAY_900} fontWeight="medium">{t(`basement_ceiling_floor_type.${type}`)}</Typography>
                </MUIDropdownItem>
              ))}
            </MUIDropdown>
          </Stack>

          {isBasementHeated && <>

            <Stack marginTop={0.8}>
              <NumericFloatField required suffix="m" inputBoxWidth="100%" label={t('soil_depth')} value={view.soilDepth} onCommit={setSoilDepth} />
            </Stack>

            <Stack marginTop={0.8}>
              <MUINumberField min={0} required suffix="m²" inputBoxWidth="100%" label={t('heated_basement_area')} value={view.heatedBasementArea} onChange={setHeatedBasementArea} />
            </Stack>

          </>}

          <Stack marginTop={0.8}>
            <MUINumberField
              min={0}
              suffix={t('cm')}
              inputBoxWidth="100%"
              label={t('insulation_basement')}
              value={view.subsequentBasementInsulation}
              onChange={setSubsequentBasementInsulation}
            />
          </Stack>

        </Stack>
      </SectionedBorderBox>

      {/* WINDOW */}
      <SectionedBorderBox title={<SectionTitle>{t('windows_title')}</SectionTitle>}>
        <Stack alignItems="flex-start" gap={1.2}>

          <MUINumberField min={0} required inputBoxWidth="100%" label={t('year_of_manufacture')} value={view.yearOfManufacture} onChange={setYearOfManufacture} />

          <Stack gap="1.8rem" width={{ xs: '100%', lg: '32rem' }} position="relative">
            <MUIDropdown
              sx={{ width: '100%' }}
              button={(_isOpen, action) => (
                <MUIDropdownInput
                  readOnly
                  required
                  label={t('window_field')}
                  type={DropdownInputTypes.SELECTOR}
                  value={t(view.windowType ? `window_type.${view.windowType}` : 'select_window_option')}
                  onClick={action}
                />
              )}
            >
              {Object.values(WindowType).map((type) => (
                <MUIDropdownItem key={type} onClick={() => setWindowType(type)}>
                  <Typography variant="text-md" color={GRAY_900} fontWeight="medium">{t(`window_type.${type}`)}</Typography>
                </MUIDropdownItem>
              ))}
            </MUIDropdown>
          </Stack>

        </Stack>
      </SectionedBorderBox>

      {/* THERMAL BRIDGES */}
      <SectionedBorderBox title={<SectionTitle>{t('thermal_bridge_title')}</SectionTitle>}>
        <Stack gap={2} flexDirection="row" flexWrap="wrap">
          {Object.values(ThermalBridgeType).map((type) => (
            <ToggleButton
              key={type}
              isSelected={view.thermalBridgeType === type}
              label={
                <Stack textAlign="center">

                  <Typography variant="text-md" fontWeight="medium" color={GRAY_900}>{t(`thermal_bridge_type.${type}`)}</Typography>

                  <Typography variant="text-xs" color={GRAY_700}>
                    <Trans t={t} i18nKey={`thermal_bridge_type_description.${type}`}><span>{'<'}</span></Trans>
                  </Typography>

                </Stack>
              }
              sx={{ width: '26.4rem', height: '10.2rem', justifyContent: 'center' }}
              onClick={() => setThermalBridge(type)}
              typographyVariant="text-md"
            />
          ))}
        </Stack>
      </SectionedBorderBox>

    </Stack>
  )
}
